/**
 * Modal para crear/editar Periodo Académico
 */

import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button, Input } from "@/components/ui";
import { DatePickerField } from "@/components/ui/DatePicker";
import { X, Loader2 } from "lucide-react";
import {
  useCreateAcademicPeriod,
  useUpdateAcademicPeriod,
} from "@/hooks/useCatalogs";
import type { AcademicPeriod } from "@/lib/services/catalogs";

const periodSchema = z.object({
  name: z.string().min(3, "El nombre debe tener al menos 3 caracteres"),
  year: z.coerce.number().min(2020, "El año debe ser mayor a 2020"),
  startDate: z.string().min(1, "La fecha de inicio es requerida"),
  endDate: z.string().min(1, "La fecha de fin es requerida"),
  isActive: z.boolean(),
});

type PeriodForm = z.infer<typeof periodSchema>;

interface AcademicPeriodModalProps {
  isOpen: boolean;
  onClose: () => void;
  period?: AcademicPeriod | null;
}

function formatDateForInput(date: Date | string | undefined): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toISOString().split("T")[0];
}

export default function AcademicPeriodModal({
  isOpen,
  onClose,
  period,
}: AcademicPeriodModalProps) {
  const isEditing = !!period;

  const createMutation = useCreateAcademicPeriod();
  const updateMutation = useUpdateAcademicPeriod();

  const currentYear = new Date().getFullYear();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
  } = useForm<PeriodForm>({
    resolver: zodResolver(periodSchema),
    defaultValues: {
      name: period?.name || `Periodo ${currentYear}`,
      year: period?.year || currentYear,
      startDate:
        formatDateForInput(period?.startDate) || `${currentYear}-02-01`,
      endDate: formatDateForInput(period?.endDate) || `${currentYear}-11-30`,
      isActive: period?.isActive || false,
    },
  });

  // Reset form when period changes
  useEffect(() => {
    if (isOpen) {
      reset({
        name: period?.name || `Periodo ${currentYear}`,
        year: period?.year || currentYear,
        startDate:
          formatDateForInput(period?.startDate) || `${currentYear}-02-01`,
        endDate: formatDateForInput(period?.endDate) || `${currentYear}-11-30`,
        isActive: period?.isActive || false,
      });
    }
  }, [period, isOpen, reset, currentYear]);

  const onSubmit = async (data: PeriodForm) => {
    try {
      const periodData = {
        name: data.name,
        year: data.year,
        startDate: new Date(data.startDate + "T12:00:00"),
        endDate: new Date(data.endDate + "T12:00:00"),
        isActive: data.isActive,
        status: "active" as const,
      };

      if (isEditing) {
        await updateMutation.mutateAsync({ id: period.id, data: periodData });
      } else {
        await createMutation.mutateAsync(periodData);
      }
      onClose();
    } catch (error) {
      // Error handled by mutation
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">
            {isEditing ? "Editar Periodo" : "Nuevo Periodo"}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors duration-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Input
            label="Nombre *"
            {...register("name")}
            error={errors.name?.message}
            placeholder="Ej: Periodo 2024"
          />

          <Input
            label="Año *"
            type="number"
            {...register("year")}
            error={errors.year?.message}
          />

          <div className="grid grid-cols-2 gap-3">
            <DatePickerField
              control={control}
              name="startDate"
              label="Fecha inicio *"
              error={errors.startDate?.message}
            />
            <DatePickerField
              control={control}
              name="endDate"
              label="Fecha fin *"
              error={errors.endDate?.message}
            />
          </div>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              {...register("isActive")}
              className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">Periodo activo</span>
          </label>
          <p className="text-xs text-gray-500">
            Al activar este periodo, se desactivarán los demás
          </p>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading} className="flex-1">
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : isEditing ? (
                "Guardar"
              ) : (
                "Crear"
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
